import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { RentPin } from '../types';
import { ReportForm } from './ReportForm';
import rentalApi from '../services/api';
import { useFocusTrap } from '../hooks/useFocusTrap';
import './PinPopup.css';

interface PinPopupProps {
  rentals: RentPin[];
  onClose: () => void;
  onDropSeekerPin?: (coords: [number, number], type: 'room' | 'property') => void;
  onPinDeleted?: () => void;
  onPinUpdated?: () => void;
  deleteToken?: string;
}

const ShareIcon = () => (
  <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

const FlagIcon = () => (
  <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" y1="22" x2="4" y2="15" />
  </svg>
);

const CloseIcon = () => (
  <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const EditIcon = () => (
  <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const TrashIcon = () => (
  <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const LinkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const PROP_TYPES = ['Basement', 'Apartment', 'Condo', 'Townhouse', 'Semi-Detached', 'Detached'];
const BHK_OPTS   = ['1', '1+', '2', '2+', '3+'];
const BATH_OPTS  = ['1', '1+', '2', '2+', '3+'];
const FURNISH    = ['Furnished', 'Semi-Furnished', 'Unfurnished'];
const PETS_OPTS  = ['Yes', 'No', 'Not Sure'];

export const PinPopup: React.FC<PinPopupProps> = ({ rentals, onClose, onDropSeekerPin, onPinDeleted, onPinUpdated, deleteToken }) => {
  const [unitIndex, setUnitIndex]     = useState(0);
  const [shareOpen, setShareOpen]     = useState(false);
  const [reporting, setReporting]     = useState(false);
  const [reportDone, setReportDone]   = useState(false);
  const [linkCopied, setLinkCopied]   = useState(false);
  const [localityRating, setLocalityRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [seekerPickOpen, setSeekerPickOpen] = useState(false);

  // Owner state
  const [isOwner, setIsOwner]         = useState(false);
  const [editing, setEditing]         = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting]       = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Edit form state
  const [editRent, setEditRent]           = useState('');
  const [editPropType, setEditPropType]   = useState('');
  const [editBhk, setEditBhk]             = useState('');
  const [editBaths, setEditBaths]         = useState('');
  const [editFurnishing, setEditFurnishing] = useState('');
  const [editPets, setEditPets]           = useState('');
  const [editDesc, setEditDesc]           = useState('');
  const [editSaving, setEditSaving]       = useState(false);
  const [editError, setEditError]         = useState('');
  const [localRental, setLocalRental]     = useState<RentPin | null>(null);

  const rental = localRental ?? rentals[unitIndex];

  const currentPinId = rentals[unitIndex]?.id;

  // Check ownership silently on mount / when pin changes
  useEffect(() => {
    setLocalRental(null);
    setEditing(false);
    setConfirmDelete(false);
    if (deleteToken) {
      setIsOwner(true);
      return;
    }
    setIsOwner(false);
    if (currentPinId) {
      rentalApi.checkRentPinOwnership(currentPinId)
        .then(res => setIsOwner(res.data.isOwner))
        .catch(() => {});
    }
  }, [currentPinId, unitIndex, deleteToken]);

  // Pre-fill edit form when editing opens
  useEffect(() => {
    if (!editing) return;
    setEditRent(String(rental.monthly_rent ?? ''));
    setEditPropType(rental.property_type ?? '');
    setEditBhk(String(rental.bedrooms ?? ''));
    setEditBaths(rental.baths ?? '');
    setEditFurnishing(rental.furnishing ?? '');
    setEditPets((rental as any).pets_allowed_status ?? '');
    setEditDesc(rental.description ?? '');
    setEditError('');
  }, [editing, rental]);

  useEffect(() => {
    const saved = localStorage.getItem(`locality-rating-${rental.id}`);
    setLocalityRating(saved ? parseInt(saved, 10) : 0);
  }, [rental.id]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleRate = (star: number) => {
    setLocalityRating(star);
    localStorage.setItem(`locality-rating-${rental.id}`, String(star));
  };

  const handleShareWhatsApp = () => {
    const text = `🏠 Rental in ${rental.city}: $${rental.monthly_rent.toLocaleString()}/mo · ${rental.bedrooms}BR${rental.baths ? ` ${rental.baths}Bath` : ''}\n📍 ${rental.address}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    setShareOpen(false);
  };

  const handleCopyLink = async () => {
    const url = `${window.location.origin}${window.location.pathname}?pin=${rental.id}`;
    try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
    setLinkCopied(true);
    setShareOpen(false);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      if (deleteToken) {
        await rentalApi.deleteRentPinByToken(rental.id, deleteToken);
      } else {
        await rentalApi.deleteRentPinByIp(rental.id);
      }
      onClose();
      onPinDeleted?.();
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to delete. Try again.');
      setDeleting(false);
    }
  };

  const handleEditSave = async () => {
    if (!editRent || parseFloat(editRent) <= 0) return setEditError('Monthly rent is required');
    if (parseFloat(editRent) > 9999) return setEditError('Monthly rent cannot exceed $9,999');
    setEditSaving(true);
    setEditError('');
    try {
      await rentalApi.updateRentPin(rental.id, {
        monthlyRent:      parseFloat(editRent),
        propertyType:     editPropType || undefined,
        bedrooms:         editBhk ? parseInt(editBhk) : undefined,
        baths:            editBaths || undefined,
        furnishing:       editFurnishing as any || undefined,
        petsAllowedStatus: editPets || undefined,
        description:      editDesc || undefined,
      });
      setLocalRental({
        ...rental,
        monthly_rent:  parseFloat(editRent),
        property_type: editPropType  || rental.property_type,
        bedrooms:      editBhk ? parseInt(editBhk) : rental.bedrooms,
        baths:         editBaths     || rental.baths,
        furnishing:    (editFurnishing || rental.furnishing) as 'Furnished' | 'Semi-Furnished' | 'Unfurnished',
        ...(editPets ? { pets_allowed_status: editPets as any } : {}),
        description:   editDesc      || rental.description,
      });
      setEditing(false);
      onPinUpdated?.();
    } catch (err: any) {
      setEditError(err.message || 'Failed to save. Try again.');
    } finally {
      setEditSaving(false);
    }
  };

  const pinnedDate = new Date(rental.created_at).toLocaleDateString('en-CA', {
    year: 'numeric', month: 'short', day: 'numeric',
  });

  type Tag = { emoji: string; text: string };
  const tags: Tag[] = [
    ...(rental.bedrooms != null ? [{ emoji: '🛏', text: `${rental.bedrooms} Bed` }] : []),
    ...(rental.baths ? [{ emoji: '🚿', text: `${rental.baths} Bath` }] : []),
    ...(rental.furnishing ? [{ emoji: '🪑', text: rental.furnishing }] : []),
    ...(rental.property_type ? rental.property_type.split(',').map(pt => ({ emoji: '🏠', text: pt.trim() })) : []),
    ...(rental.square_footage ? [{ emoji: '📐', text: `${rental.square_footage} sqft` }] : []),
    ...(rental.residents_type ? [{ emoji: '👥', text: rental.residents_type }] : []),
    ...(rental.pets_allowed ? [{ emoji: '🐾', text: 'Pets OK' }] : []),
    ...(rental.includes_utilities ? [{ emoji: '💡', text: 'Utilities Incl.' }] : []),
    ...(rental.includes_hydro ? [{ emoji: '⚡', text: 'Hydro Incl.' }] : []),
    ...(rental.deposit_paid ? [{ emoji: '🔑', text: 'Deposit Req.' }] : []),
    ...(rental.transit_distance ? [{ emoji: '🚌', text: rental.transit_distance }] : []),
  ];

  const trapRef = useFocusTrap(true);

  const Tgl = ({ opts, val, onSet, label }: { opts: string[]; val: string; onSet: (v: string) => void; label: string }) => (
    <div className="pp-edit-toggle-group" role="group" aria-label={label}>
      {opts.map(o => (
        <button type="button" key={o}
          className={`pp-edit-tgl${val === o ? ' on' : ''}`}
          aria-pressed={val === o}
          onClick={() => onSet(val === o ? '' : o)}>
          {o}
        </button>
      ))}
    </div>
  );

  const titleId = 'pp-title';

  const popup = (
    <div className="pp-backdrop" onClick={onClose} aria-hidden="true">
      <div
        className="pp-card"
        ref={el => { trapRef.current = el; }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={e => e.stopPropagation()}
      >

        {reporting ? (
          <div className="pp-report-wrapper" aria-label="Report listing">
            <ReportForm
              rentPinId={rental.id}
              onSuccess={() => { setReporting(false); setReportDone(true); }}
              onCancel={() => setReporting(false)}
            />
          </div>

        ) : editing ? (
          /* ── Inline edit panel ── */
          <div className="pp-edit-panel">
            <div className="pp-edit-header">
              <span id={titleId} className="pp-edit-title">Edit Your Pin</span>
              <button className="pp-action-btn pp-action-close" onClick={() => setEditing(false)} aria-label="Cancel editing"><CloseIcon aria-hidden="true" /></button>
            </div>

            <div className="pp-edit-addr"><span aria-hidden="true">📍</span> {rental.address}</div>

            <div className="pp-edit-body">
              {editError && <div className="pp-edit-error" role="alert">{editError}</div>}

              <div className="pp-edit-field">
                <label htmlFor="pp-edit-rent" className="pp-edit-label">Monthly Rent (CAD) <span aria-hidden="true">*</span></label>
                <div className="pp-edit-rent-row">
                  <span className="pp-edit-prefix" aria-hidden="true">$</span>
                  <input id="pp-edit-rent" className="pp-edit-input" type="number" value={editRent}
                    onChange={e => setEditRent(e.target.value)} placeholder="e.g. 2200" min="1" max="9999"
                    aria-required="true" aria-describedby={editError ? 'pp-edit-err' : undefined} />
                  <span className="pp-edit-suffix" aria-hidden="true">/mo</span>
                </div>
              </div>

              <div className="pp-edit-field">
                <span id="pp-proptype-label" className="pp-edit-label">Property Type</span>
                <div className="pp-edit-chip-group" role="group" aria-labelledby="pp-proptype-label">
                  {PROP_TYPES.map(pt => (
                    <button type="button" key={pt}
                      className={`pp-edit-chip${editPropType === pt ? ' on' : ''}`}
                      aria-pressed={editPropType === pt}
                      onClick={() => setEditPropType(editPropType === pt ? '' : pt)}>
                      {pt}
                    </button>
                  ))}
                </div>
              </div>

              <div className="pp-edit-field">
                <label className="pp-edit-label" id="pp-bhk-label">Bedrooms</label>
                <Tgl opts={BHK_OPTS} val={editBhk} onSet={setEditBhk} label="Bedrooms" />
              </div>

              <div className="pp-edit-field">
                <label className="pp-edit-label" id="pp-baths-label">Baths</label>
                <Tgl opts={BATH_OPTS} val={editBaths} onSet={setEditBaths} label="Baths" />
              </div>

              <div className="pp-edit-field">
                <label className="pp-edit-label" id="pp-furnish-label">Furnishing</label>
                <Tgl opts={FURNISH} val={editFurnishing} onSet={setEditFurnishing} label="Furnishing" />
              </div>

              <div className="pp-edit-field">
                <label className="pp-edit-label" id="pp-pets-label">Pets</label>
                <Tgl opts={PETS_OPTS} val={editPets} onSet={setEditPets} label="Pets allowed" />
              </div>

              <div className="pp-edit-field">
                <label htmlFor="pp-edit-desc" className="pp-edit-label">Description <span className="pp-edit-opt">optional · 200 chars</span></label>
                <textarea id="pp-edit-desc" className="pp-edit-textarea" rows={2} value={editDesc}
                  onChange={e => setEditDesc(e.target.value.slice(0, 200))}
                  placeholder="e.g. Bright corner unit, updated kitchen…"
                  aria-describedby="pp-desc-cc" />
                <span id="pp-desc-cc" className="pp-edit-cc" aria-live="polite">{editDesc.length}/200</span>
              </div>
            </div>

            <div className="pp-edit-actions">
              <button className="pp-edit-cancel" onClick={() => setEditing(false)} disabled={editSaving}>Cancel</button>
              <button className="pp-edit-save" onClick={handleEditSave} disabled={editSaving}
                aria-busy={editSaving}>
                {editSaving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>

        ) : confirmDelete ? (
          /* ── Confirm delete ── */
          <div className="pp-delete-panel" role="alertdialog" aria-labelledby="pp-del-title" aria-describedby="pp-del-sub">
            <div className="pp-delete-icon" aria-hidden="true">🗑️</div>
            <div id="pp-del-title" className="pp-delete-title">Delete this pin?</div>
            <div id="pp-del-sub" className="pp-delete-sub">
              This will permanently remove the rental at<br />
              <strong>{rental.address}</strong>
            </div>
            {deleteError && <div className="pp-edit-error" role="alert">{deleteError}</div>}
            <div className="pp-delete-actions">
              <button className="pp-edit-cancel" onClick={() => setConfirmDelete(false)} disabled={deleting}>Cancel</button>
              <button className="pp-delete-confirm" onClick={handleDelete} disabled={deleting} aria-busy={deleting}>
                {deleting ? 'Deleting…' : 'Yes, Delete'}
              </button>
            </div>
          </div>

        ) : (
          <>
            {/* ── Header ── */}
            <div className="pp-header">
              <div className="pp-rent">
                <span id={titleId} className="pp-rent-amount">${rental.monthly_rent.toLocaleString()}<span className="pp-rent-period">/month</span></span>
              </div>

              <div className="pp-header-actions">
                {/* Owner actions */}
                {isOwner && (
                  <>
                    {!deleteToken && (
                      <button className="pp-action-btn pp-action-edit" onClick={() => setEditing(true)} title="Edit your pin" aria-label="Edit">
                        <EditIcon />
                      </button>
                    )}
                    <button className="pp-action-btn pp-action-delete" onClick={() => setConfirmDelete(true)} title="Delete your pin" aria-label="Delete">
                      <TrashIcon />
                    </button>
                  </>
                )}

                {/* Share */}
                <div className="pp-share-wrapper">
                  <button className="pp-action-btn" onClick={() => setShareOpen(s => !s)} title="Share" aria-label="Share">
                    <ShareIcon />
                  </button>
                  {shareOpen && (
                    <div className="pp-share-menu">
                      <button className="pp-share-item pp-share-whatsapp" onClick={handleShareWhatsApp}>
                        <WhatsAppIcon /> WhatsApp
                      </button>
                      <button className="pp-share-item" onClick={handleCopyLink}>
                        <LinkIcon /> {linkCopied ? '✓ Copied!' : 'Copy Link'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Report */}
                <button className="pp-action-btn pp-action-flag" onClick={() => setReporting(true)} title="Report listing" aria-label="Report">
                  <FlagIcon />
                </button>

                {/* Close */}
                <button className="pp-action-btn pp-action-close" onClick={onClose} title="Close" aria-label="Close">
                  <CloseIcon />
                </button>
              </div>
            </div>

            {/* Owner badge */}
            {isOwner && (
              <div className="pp-owner-badge">✓ Your pin</div>
            )}

            {/* ── Multi-unit navigator ── */}
            {rentals.length > 1 && (
              <div className="pp-unit-nav">
                <button disabled={unitIndex === 0} onClick={() => setUnitIndex(i => i - 1)} aria-label="Previous unit">‹</button>
                <span>Unit {unitIndex + 1} of {rentals.length}</span>
                <button disabled={unitIndex === rentals.length - 1} onClick={() => setUnitIndex(i => i + 1)} aria-label="Next unit">›</button>
              </div>
            )}

            {/* ── Address ── */}
            <div className="pp-address">
              <span aria-hidden="true">📍</span> {rental.address}{rental.city ? `, ${rental.city}` : ''}{rental.postal_code ? ` ${rental.postal_code}` : ''}
            </div>

            {/* ── Detail tags ── */}
            <ul className="pp-tags" aria-label="Property details">
              {tags.map((tag, i) => (
                <li key={i} className="pp-tag">
                  <span className="pp-tag-emoji" aria-hidden="true">{tag.emoji}</span>
                  <span>{tag.text}</span>
                </li>
              ))}
            </ul>

            {/* ── Description ── */}
            {rental.description && (
              <p className="pp-description">{rental.description}</p>
            )}

            {/* ── Seeker CTA ── */}
            {onDropSeekerPin && (
              seekerPickOpen ? (
                <div className="pp-seeker-pick">
                  <div className="pp-seeker-pick-title">What are you looking for?</div>
                  <div className="pp-seeker-pick-btns">
                    <button
                      className="pp-seeker-pick-btn"
                      onClick={() => { onDropSeekerPin([rental.longitude, rental.latitude], 'room'); onClose(); }}
                    >
                      <span className="pp-seeker-pick-icon">🛏</span>
                      <span className="pp-seeker-pick-label">A Room</span>
                      <span className="pp-seeker-pick-sub">Shared / roommate setup</span>
                    </button>
                    <button
                      className="pp-seeker-pick-btn"
                      onClick={() => { onDropSeekerPin([rental.longitude, rental.latitude], 'property'); onClose(); }}
                    >
                      <span className="pp-seeker-pick-icon">🏠</span>
                      <span className="pp-seeker-pick-label">Whole Unit</span>
                      <span className="pp-seeker-pick-sub">Apartment, condo, house…</span>
                    </button>
                  </div>
                  <button className="pp-seeker-pick-cancel" onClick={() => setSeekerPickOpen(false)}>Cancel</button>
                </div>
              ) : (
                <button className="pp-seeker-cta" onClick={() => setSeekerPickOpen(true)}>
                  <span className="pp-seeker-cta-icon" aria-hidden="true">🔍</span>
                  <span className="pp-seeker-cta-text">
                    <span className="pp-seeker-cta-title">Drop a seeker pin nearby for auto matching</span>
                    <span className="pp-seeker-cta-sub">We'll email you when this or nearby flats match your budget and preferences.</span>
                  </span>
                  <span className="pp-seeker-cta-arrow" aria-hidden="true">→</span>
                </button>
              )
            )}

            {/* ── Report-done toast ── */}
            {reportDone && (
              <div className="pp-report-done" role="status" aria-live="polite"><span aria-hidden="true">✓</span> Report submitted — thank you</div>
            )}

            {/* ── Reports warning ── */}
            {rental.reports > 0 && (
              <div className="pp-reported" role="note">
                <span aria-hidden="true">⚠️</span> Flagged {rental.reports} time{rental.reports !== 1 ? 's' : ''} by community
              </div>
            )}

            {/* ── Footer ── */}
            <div className="pp-footer">
              <div className="pp-date">📅 {pinnedDate}</div>
              <div className="pp-rating">
                <span className="pp-rating-label">Rate locality</span>
                <div className="pp-stars">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      className={`pp-star${star <= (hoverRating || localityRating) ? ' pp-star-active' : ''}`}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      onClick={() => handleRate(star)}
                      title={`${star} star${star > 1 ? 's' : ''}`}
                      aria-label={`Rate locality ${star} stars`}
                    >★</button>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(popup, document.body);
};
